import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Building2, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CreateClientDialog } from "./components/CreateClientDialog";
import { useNavigate } from 'react-router-dom';
import { useProject } from "@/hooks/useProject";

export default function ClientList() {
  const navigate = useNavigate();
  const { projectClient, currentProject } = useProject();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients', currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from('clientes')
        .select('*')
        .eq('project_id', projectClient.getProjectId());
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      return data;
    },
  });

  // Query para obtener el conteo de lanzamientos por cliente
  const { data: launchCounts } = useQuery({
    queryKey: ['client-launch-counts', currentProject],
    queryFn: async () => {
      const { data, error } = await projectClient
        .from('form_launches')
        .select('id_cliente')
        .eq('project_id', projectClient.getProjectId());
      
      if (error) throw error;
      
      // Contar lanzamientos por cliente
      const counts: Record<number, number> = {};
      data?.forEach(launch => {
        counts[launch.id_cliente] = (counts[launch.id_cliente] || 0) + 1;
      });
      
      return counts;
    },
  });

  // Filtrar clientes por búsqueda
  const filteredClients = clients?.filter(client =>
    client.nombre.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Clientes</h1>
          <CreateClientDialog />
        </div>
        <div className="flex gap-4 mb-6">
          <Skeleton className="h-10 flex-1" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-muted-foreground mt-1">
            {filteredClients?.length || 0} cliente{filteredClients?.length !== 1 ? 's' : ''} {searchQuery && 'encontrado' + (filteredClients?.length !== 1 ? 's' : '')}
          </p>
        </div>
        <CreateClientDialog />
      </div>

      {/* Barra de búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar clientes por nombre..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Estado vacío */}
      {(!filteredClients || filteredClients.length === 0) && !searchQuery && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Building2 className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No hay clientes todavía</h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              Comienza creando tu primer cliente para gestionar tus lanzamientos y reportes.
            </p>
            <CreateClientDialog />
          </CardContent>
        </Card>
      )}

      {/* Resultados de búsqueda vacíos */}
      {searchQuery && filteredClients?.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Search className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No se encontraron resultados</h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              No hay clientes que coincidan con "{searchQuery}"
            </p>
            <Button variant="outline" onClick={() => setSearchQuery("")}>
              Limpiar búsqueda
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Grid de clientes */}
      {filteredClients && filteredClients.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredClients.map((client) => (
            <Card
              key={client.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-1 group"
              onClick={() => navigate(`/admin/clients/${client.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    {client.logo ? (
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mb-2 p-1">
                        <img
                          src={client.logo}
                          alt={`Logo de ${client.nombre}`}
                          className="w-full h-full object-contain transition-transform group-hover:scale-110"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mb-2">
                        <Building2 className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  {launchCounts && launchCounts[client.id] > 0 && (
                    <Badge variant="secondary" className="gap-1 text-xs px-2 py-0">
                      <Rocket className="w-3 h-3" />
                      {launchCounts[client.id]}
                    </Badge>
                  )}
                </div>
                
                <h3 className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors line-clamp-1">
                  {client.nombre}
                </h3>
                
                {client.mision && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {client.mision}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
